import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, Maximize2, Mic, MicOff, Minimize2, MonitorUp, Paperclip, Phone, PhoneOff, RefreshCw, Send, Settings, Video, VideoOff, X } from "lucide-react";
import { getIceServers } from "./services/api";
import apiRoutes from "./services/apiRoutes";
import { sendCallDebugLog } from "./services/callDebugLogger";

const fileChunkSize = 16 * 1024;
const maxFileSize = 15 * 1024 * 1024;
const maxBufferedFileBytes = 1024 * 1024;
const debugWebRTC = true;

const rtcConfig = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
    ],
    iceTransportPolicy: "all",
};

function getMediaPermissionMessage(err) {
    const permissionErrors = ["NotAllowedError", "PermissionDeniedError", "SecurityError"];
    if (permissionErrors.includes(err?.name) || /permission denied|notallowed/i.test(err?.message || "")) {
        return "Microphone access is blocked. Allow microphone access for this site in the browser settings, then tap Retry microphone.";
    }
    if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        return "No microphone was found on this device.";
    }
    if (err?.name === "NotReadableError" || err?.name === "TrackStartError") {
        return "The microphone is already in use by another app or browser tab.";
    }
    return err?.message || "Could not access microphone.";
}

function createRoomId(userA, userB) {
    return [userA, userB].sort().join("__");
}

function formatFileSize(size) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function createMessageId() {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createFilePacket(fileId, chunkBuffer) {
    const header = new TextEncoder().encode(`${JSON.stringify({ fileId })}\n`);
    const chunk = new Uint8Array(chunkBuffer);
    const packet = new Uint8Array(header.byteLength + chunk.byteLength);
    packet.set(header, 0);
    packet.set(chunk, header.byteLength);
    return packet.buffer;
}

function parseFilePacket(packetBuffer) {
    const packet = new Uint8Array(packetBuffer);
    const headerEnd = packet.indexOf(10);
    if (headerEnd === -1) {
        throw new Error("Invalid file packet");
    }

    const header = JSON.parse(new TextDecoder().decode(packet.slice(0, headerEnd)));
    return {
        chunk: packet.slice(headerEnd + 1),
        fileId: header.fileId,
    };
}

function waitForBufferedAmount(channel) {
    if (channel.bufferedAmount <= maxBufferedFileBytes) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        channel.onbufferedamountlow = () => {
            channel.onbufferedamountlow = null;
            resolve();
        };
        channel.onerror = () => reject(new Error("Data channel failed while sending file"));
    });
}

function logRTC(event, payload = {}) {
    if (debugWebRTC) {
        console.log(`[rtc] ${event}`, payload);
    }
    sendCallDebugLog("rtc", event, payload);
}

function WebRTCCallSession({ user, contact, isOpen, onClose, autoStart = false, roomId: providedRoomId = "", isInitiator = false }) {
    const [status, setStatus] = useState("Idle");
    const [error, setError] = useState("");
    const [isMuted, setIsMuted] = useState(false);
    const [isSharingScreen, setIsSharingScreen] = useState(false);
    const [isCameraEnabled, setIsCameraEnabled] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [isChatReady, setIsChatReady] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [isSendingFile, setIsSendingFile] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [mediaDevices, setMediaDevices] = useState({ audioinput: [], audiooutput: [], videoinput: [] });
    const [selectedMicId, setSelectedMicId] = useState("");
    const [selectedCameraId, setSelectedCameraId] = useState("");
    const [selectedSpeakerId, setSelectedSpeakerId] = useState("");
    const [isAudioPermissionBlocked, setIsAudioPermissionBlocked] = useState(false);
    const [isRemoteExpanded, setIsRemoteExpanded] = useState(false);

    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const remotePanelRef = useRef(null);
    const fileInputRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(new MediaStream());
    const screenTrackRef = useRef(null);
    const cameraTrackRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const socketRef = useRef(null);
    const rtcConfigRef = useRef(rtcConfig);
    const dataChannelRef = useRef(null);
    const makingOfferRef = useRef(false);
    const canNegotiateRef = useRef(false);
    const peerReadyHandledRef = useRef(false);
    const ignoreOfferRef = useRef(false);
    const answeredOfferSdpRef = useRef("");
    const pendingSignalsRef = useRef([]);
    const pendingIceCandidatesRef = useRef([]);
    const localIceCandidatesRef = useRef([]);
    const incomingFilesRef = useRef(new Map());
    const objectUrlsRef = useRef([]);

    const roomId = useMemo(() => (
        providedRoomId || createRoomId(user?.username || "", contact?.username || "")
    ), [contact?.username, providedRoomId, user?.username]);
    const isPolite = useMemo(
        () => (user?.username || "").localeCompare(contact?.username || "") > 0,
        [user?.username, contact?.username]
    );

    const loadRtcConfig = useCallback(async () => {
        try {
            const { data } = await getIceServers();
            rtcConfigRef.current = {
                iceServers: data.iceServers || rtcConfig.iceServers,
                iceTransportPolicy: data.iceTransportPolicy || "all",
            };
            logRTC("ice-config-loaded", {
                iceServers: rtcConfigRef.current.iceServers.map((server) => server.urls),
                iceTransportPolicy: rtcConfigRef.current.iceTransportPolicy,
                roomId,
            });
        } catch (err) {
            rtcConfigRef.current = rtcConfig;
            logRTC("ice-config-fallback", { message: err.message, roomId });
        }
    }, [roomId]);

    const loadMediaDevices = useCallback(async () => {
        if (!navigator.mediaDevices?.enumerateDevices) {
            return;
        }

        const devices = await navigator.mediaDevices.enumerateDevices();
        const nextDevices = {
            audioinput: devices.filter((device) => device.kind === "audioinput"),
            audiooutput: devices.filter((device) => device.kind === "audiooutput"),
            videoinput: devices.filter((device) => device.kind === "videoinput"),
        };
        setMediaDevices(nextDevices);
        setSelectedMicId((current) => current || nextDevices.audioinput[0]?.deviceId || "");
        setSelectedCameraId((current) => current || nextDevices.videoinput[0]?.deviceId || "");
        setSelectedSpeakerId((current) => current || nextDevices.audiooutput[0]?.deviceId || "");
    }, []);

    const sendSignal = useCallback((message) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            logRTC("signal-queued", { roomId, type: message.type });
            pendingSignalsRef.current.push(message);
            return;
        }
        logRTC("signal-send", { roomId, type: message.type });
        socket.send(JSON.stringify(message));
    }, [roomId]);

    const resendCurrentOffer = useCallback((pc, trigger) => {
        if (pc.localDescription?.type !== "offer" || pc.signalingState === "stable") {
            logRTC("offer-resend-skipped", { roomId, signalingState: pc.signalingState, trigger });
            return false;
        }

        logRTC("offer-resend-current", { roomId, signalingState: pc.signalingState, trigger });
        sendSignal({ type: "offer", description: pc.localDescription });
        localIceCandidatesRef.current.forEach((candidate) => {
            sendSignal({ type: "ice-candidate", candidate });
        });
        logRTC("ice-resend-local", { count: localIceCandidatesRef.current.length, roomId, trigger });
        return true;
    }, [roomId, sendSignal]);

    const flushPendingSignals = useCallback(() => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            return;
        }

        const pendingSignals = pendingSignalsRef.current.splice(0);
        logRTC("signal-flush", { count: pendingSignals.length, roomId });
        pendingSignals.forEach((message) => socket.send(JSON.stringify(message)));
    }, [roomId]);

    const appendChatMessage = useCallback((message) => {
        setChatMessages((messages) => [
            ...messages,
            {
                id: createMessageId(),
                ...message,
            },
        ]);
    }, []);

    const updateChatMessage = useCallback((messageId, patch) => {
        setChatMessages((messages) => messages.map((message) => (
            message.id === messageId ? { ...message, ...patch } : message
        )));
    }, []);

    const handleIncomingFileChunk = useCallback((packetBuffer) => {
        const { chunk, fileId } = parseFilePacket(packetBuffer);
        const transfer = incomingFilesRef.current.get(fileId);
        if (!transfer) return;

        transfer.chunks.push(chunk);
        transfer.receivedBytes += chunk.byteLength;
        updateChatMessage(transfer.messageId, {
            file: {
                name: transfer.name,
                progress: Math.min(100, Math.round((transfer.receivedBytes / transfer.size) * 100)),
                size: transfer.size,
                status: "Receiving",
            },
        });
    }, [updateChatMessage]);

    const handleIncomingFileMessage = useCallback((payload) => {
        if (payload.kind === "file-start") {
            const messageId = createMessageId();
            incomingFilesRef.current.set(payload.fileId, {
                chunks: [],
                messageId,
                name: payload.name,
                receivedBytes: 0,
                size: payload.size,
                type: payload.fileType || "application/octet-stream",
            });

            setChatMessages((messages) => [
                ...messages,
                {
                    id: messageId,
                    direction: "incoming",
                    file: {
                        name: payload.name,
                        progress: 0,
                        size: payload.size,
                        status: "Receiving",
                    },
                    from: contact?.username || "Peer",
                    sentAt: payload.sentAt || new Date().toISOString(),
                },
            ]);
            return;
        }

        if (payload.kind === "file-end") {
            const transfer = incomingFilesRef.current.get(payload.fileId);
            if (!transfer) return;

            const blob = new Blob(transfer.chunks, { type: transfer.type });
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.push(url);
            incomingFilesRef.current.delete(payload.fileId);
            updateChatMessage(transfer.messageId, {
                file: {
                    name: transfer.name,
                    progress: 100,
                    size: transfer.size,
                    status: "Ready",
                    url,
                },
            });
        }
    }, [contact?.username, updateChatMessage]);

    const setupDataChannel = useCallback((channel) => {
        dataChannelRef.current = channel;
        channel.binaryType = "arraybuffer";
        channel.bufferedAmountLowThreshold = 256 * 1024;
        logRTC("datachannel-setup", { label: channel.label, roomId });
        channel.onopen = () => {
            logRTC("datachannel-open", { label: channel.label, roomId });
            setIsChatReady(true);
        };
        channel.onclose = () => {
            logRTC("datachannel-close", { label: channel.label, roomId });
            setIsChatReady(false);
        };
        channel.onerror = () => {
            logRTC("datachannel-error", { label: channel.label, roomId });
            setIsChatReady(false);
        };
        channel.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                handleIncomingFileChunk(event.data);
                return;
            }

            let payload = { body: event.data };
            try {
                payload = JSON.parse(event.data);
            } catch {
                payload = { body: event.data };
            }

            if (payload.kind?.startsWith("file-")) {
                handleIncomingFileMessage(payload);
                return;
            }

            appendChatMessage({
                from: contact?.username || "Peer",
                body: payload.body || "",
                sentAt: payload.sentAt || new Date().toISOString(),
                direction: "incoming",
            });
        };
    }, [appendChatMessage, contact?.username, handleIncomingFileChunk, handleIncomingFileMessage, roomId]);

    const ensureDataChannel = useCallback((pc) => {
        if (dataChannelRef.current) {
            return dataChannelRef.current;
        }

        const channel = pc.createDataChannel("call-chat", { ordered: true });
        logRTC("datachannel-create", { roomId });
        setupDataChannel(channel);
        return channel;
    }, [roomId, setupDataChannel]);

    const replaceLocalTrack = useCallback((kind, nextTrack) => {
        const pc = peerConnectionRef.current;
        const currentTrack = kind === "audio" ? localStreamRef.current?.getAudioTracks()[0] : cameraTrackRef.current;
        const sender = pc?.getSenders().find((item) => item.track?.kind === kind);

        if (sender) {
            sender.replaceTrack(nextTrack);
        } else if (pc && nextTrack) {
            pc.addTrack(nextTrack, new MediaStream([nextTrack]));
        }

        if (currentTrack && currentTrack !== nextTrack) {
            currentTrack.stop();
            localStreamRef.current?.removeTrack(currentTrack);
        }

        if (!localStreamRef.current) {
            localStreamRef.current = new MediaStream();
        }
        if (nextTrack && !localStreamRef.current.getTracks().includes(nextTrack)) {
            localStreamRef.current.addTrack(nextTrack);
        }
    }, []);

    const stopLocalTracks = useCallback(() => {
        localStreamRef.current?.getTracks().forEach((track) => track.stop());
        screenTrackRef.current?.stop();
        cameraTrackRef.current?.stop();
        localStreamRef.current = null;
        screenTrackRef.current = null;
        cameraTrackRef.current = null;
        setIsSharingScreen(false);
        setIsCameraEnabled(false);
    }, []);

    const cleanup = useCallback(() => {
        logRTC("cleanup", { roomId });
        sendSignal({ type: "leave" });
        dataChannelRef.current?.close();
        socketRef.current?.close();
        peerConnectionRef.current?.close();
        dataChannelRef.current = null;
        socketRef.current = null;
        peerConnectionRef.current = null;
        canNegotiateRef.current = false;
        peerReadyHandledRef.current = false;
        answeredOfferSdpRef.current = "";
        pendingSignalsRef.current = [];
        pendingIceCandidatesRef.current = [];
        localIceCandidatesRef.current = [];
        incomingFilesRef.current.clear();
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
        remoteStreamRef.current = new MediaStream();
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
        if (localVideoRef.current) localVideoRef.current.srcObject = null;
        stopLocalTracks();
        setIsChatReady(false);
        setHasStarted(false);
        setIsSendingFile(false);
        setStatus("Idle");
    }, [roomId, sendSignal, stopLocalTracks]);

    const createPeerConnection = useCallback(() => {
        logRTC("peer-create", { isInitiator, isPolite, roomId });
        const pc = new RTCPeerConnection(rtcConfigRef.current);
        peerConnectionRef.current = pc;

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                localIceCandidatesRef.current.push(event.candidate);
                logRTC("ice-local", { candidateType: event.candidate.type, roomId });
                sendSignal({ type: "ice-candidate", candidate: event.candidate });
            }
        };

        pc.ontrack = (event) => {
            logRTC("track-remote", { count: event.streams[0]?.getTracks().length || 0, roomId });
            event.streams[0].getTracks().forEach((track) => {
                remoteStreamRef.current.addTrack(track);
            });
            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = remoteStreamRef.current;
            }
            setStatus("Connected");
        };

        pc.ondatachannel = (event) => {
            logRTC("datachannel-received", { label: event.channel.label, roomId });
            setupDataChannel(event.channel);
        };

        pc.onconnectionstatechange = () => {
            logRTC("connection-state", { roomId, state: pc.connectionState });
            setStatus(pc.connectionState);
        };

        pc.onnegotiationneeded = async () => {
            try {
                logRTC("negotiation-needed", { roomId, signalingState: pc.signalingState });
                if (!canNegotiateRef.current) {
                    logRTC("negotiation-deferred-waiting-for-peer", { roomId });
                    return;
                }
                if (makingOfferRef.current || pc.signalingState !== "stable") {
                    logRTC("negotiation-skipped-nonstable", { roomId, signalingState: pc.signalingState });
                    return;
                }

                makingOfferRef.current = true;
                await pc.setLocalDescription(await pc.createOffer());
                logRTC("renegotiation-offer-created", { roomId });
                sendSignal({ type: "offer", description: pc.localDescription });
            } catch (err) {
                logRTC("negotiation-error", { message: err.message, roomId });
                setError(err.message);
            } finally {
                makingOfferRef.current = false;
            }
        };

        return pc;
    }, [isInitiator, isPolite, roomId, sendSignal, setupDataChannel]);

    const ensureLocalAudio = useCallback(async () => {
        if (localStreamRef.current) {
            return localStreamRef.current;
        }

        const audio = selectedMicId ? { deviceId: { exact: selectedMicId } } : true;
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error("Media devices are not available in this browser.");
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
            setIsAudioPermissionBlocked(false);
            setError("");
            localStreamRef.current = stream;
            stream.getTracks().forEach((track) => {
                peerConnectionRef.current.addTrack(track, stream);
                track.enabled = !isMuted;
            });
            await loadMediaDevices();
            return stream;
        } catch (err) {
            const message = getMediaPermissionMessage(err);
            setIsAudioPermissionBlocked(true);
            setError(message);
            logRTC("audio-permission-error", { message, name: err?.name, roomId });
            throw new Error(message);
        }
    }, [isMuted, loadMediaDevices, roomId, selectedMicId]);

    const handleSignal = useCallback(async (event) => {
        const message = JSON.parse(event.data);
        logRTC("signal-receive", { from: message.from, roomId, type: message.type });
        const pc = peerConnectionRef.current;
        if (!pc || message.from === user.username) {
            return;
        }

        try {
            if (message.type === "offer" || message.type === "answer") {
                const description = message.description;
                if (description.type === "answer" && !["have-local-offer", "have-local-pranswer"].includes(pc.signalingState)) {
                    logRTC("answer-ignored-wrong-state", { roomId, signalingState: pc.signalingState });
                    return;
                }
                if (description.type === "offer" && answeredOfferSdpRef.current === description.sdp && pc.signalingState === "stable") {
                    logRTC("offer-duplicate-ignored", { roomId, signalingState: pc.signalingState });
                    return;
                }
                const offerCollision =
                    description.type === "offer" &&
                    (makingOfferRef.current || pc.signalingState !== "stable");

                ignoreOfferRef.current = !isPolite && offerCollision;
                if (ignoreOfferRef.current) {
                    logRTC("offer-ignored", { from: message.from, roomId });
                    return;
                }

                await pc.setRemoteDescription(description);
                canNegotiateRef.current = true;
                logRTC("remote-description-set", { roomId, type: description.type });
                const pendingIceCandidates = pendingIceCandidatesRef.current.splice(0);
                for (const candidate of pendingIceCandidates) {
                    await pc.addIceCandidate(candidate);
                }

                if (description.type === "offer") {
                    await ensureLocalAudio();
                    await pc.setLocalDescription(await pc.createAnswer());
                    answeredOfferSdpRef.current = description.sdp || "";
                    logRTC("answer-created", { roomId });
                    sendSignal({ type: "answer", description: pc.localDescription });
                }
            }

            if ((message.type === "ready" || message.type === "peer-joined") && isInitiator) {
                if (peerReadyHandledRef.current) {
                    logRTC("peer-ready-ignored-duplicate", {
                        roomId,
                        signalingState: pc.signalingState,
                        trigger: message.type,
                    });
                    resendCurrentOffer(pc, `${message.type}-duplicate`);
                    return;
                }
                peerReadyHandledRef.current = true;
                logRTC("peer-ready-handled", {
                    isInitiator,
                    isPolite,
                    roomId,
                    signalingState: pc.signalingState,
                    trigger: message.type,
                });
                canNegotiateRef.current = true;
                await ensureLocalAudio();
                ensureDataChannel(pc);
                makingOfferRef.current = true;
                if (pc.signalingState === "stable") {
                    await pc.setLocalDescription(await pc.createOffer());
                    logRTC("offer-created", { roomId });
                } else if (pc.localDescription?.type === "offer") {
                    logRTC("offer-resend-current", { roomId, signalingState: pc.signalingState });
                }

                resendCurrentOffer(pc, message.type);
                makingOfferRef.current = false;
            }

            if (message.type === "ice-candidate" && message.candidate) {
                try {
                    if (!pc.remoteDescription) {
                        logRTC("ice-queued-remote-description-missing", { roomId });
                        pendingIceCandidatesRef.current.push(message.candidate);
                        return;
                    }
                    await pc.addIceCandidate(message.candidate);
                    logRTC("ice-added", { roomId });
                } catch (err) {
                    if (!ignoreOfferRef.current) {
                        throw err;
                    }
                }
            }

            if (message.type === "screen-share-started") {
                setStatus("Remote screen sharing");
            }

            if (message.type === "screen-share-stopped") {
                setStatus("Connected");
            }

            if (message.type === "peer-left" || message.type === "leave") {
                setStatus("Peer left");
            }
        } catch (err) {
            makingOfferRef.current = false;
            logRTC("signal-error", { message: err.message, roomId });
            setError(err.message);
        }
    }, [ensureDataChannel, ensureLocalAudio, isInitiator, isPolite, resendCurrentOffer, roomId, sendSignal, user.username]);

    const startCall = useCallback(async () => {
        setError("");
        setStatus("Connecting");
        logRTC("start", { isInitiator, isPolite, roomId, user: user.username, contact: contact?.username });

        await loadRtcConfig();
        const pc = peerConnectionRef.current || createPeerConnection();
        const socket = new WebSocket(`${apiRoutes.CHAT_WS_URL}${apiRoutes.chat.callSocket(roomId, user.username)}`);
        socketRef.current = socket;
        setHasStarted(true);
        socket.onopen = async () => {
            logRTC("ws-open", { roomId, user: user.username });
            setStatus("Waiting for peer");
            flushPendingSignals();
            try {
                await ensureLocalAudio();
                sendSignal({ type: "ready" });
            } catch (err) {
                setStatus("Microphone permission needed");
                logRTC("ready-skipped-audio-permission", { message: err.message, roomId });
            }
        };
        socket.onmessage = handleSignal;
        socket.onerror = () => {
            logRTC("ws-error", { roomId, user: user.username });
            setError("Signaling connection failed");
        };
        socket.onclose = () => {
            logRTC("ws-close", { roomId, state: pc.connectionState, user: user.username });
            if (pc.connectionState !== "connected") {
                setStatus("Signaling closed");
            }
        };
    }, [contact?.username, createPeerConnection, ensureLocalAudio, flushPendingSignals, handleSignal, isInitiator, isPolite, loadRtcConfig, roomId, sendSignal, user.username]);

    const retryMicrophone = async () => {
        const pc = peerConnectionRef.current;
        if (!pc) return;

        setError("");
        setStatus("Requesting microphone");
        try {
            await ensureLocalAudio();
            if (isInitiator && canNegotiateRef.current && pc.signalingState === "stable") {
                makingOfferRef.current = true;
                await pc.setLocalDescription(await pc.createOffer());
                makingOfferRef.current = false;
                resendCurrentOffer(pc, "microphone-retry");
            } else {
                sendSignal({ type: "ready" });
            }
            setStatus(pc.connectionState === "connected" ? "Connected" : "Waiting for peer");
        } catch (err) {
            makingOfferRef.current = false;
            setStatus("Microphone permission needed");
            setError(err.message);
        }
    };

    const switchMicrophone = async (deviceId) => {
        setSelectedMicId(deviceId);
        if (!peerConnectionRef.current || !localStreamRef.current) {
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: deviceId ? { deviceId: { exact: deviceId } } : true,
                video: false,
            });
            const [track] = stream.getAudioTracks();
            track.enabled = !isMuted;
            replaceLocalTrack("audio", track);
            await loadMediaDevices();
        } catch (err) {
            setError(err.message || "Could not switch microphone");
        }
    };

    const switchCamera = async (deviceId) => {
        setSelectedCameraId(deviceId);
        if (!cameraTrackRef.current) {
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: deviceId ? { deviceId: { exact: deviceId } } : true,
            });
            const [track] = stream.getVideoTracks();
            replaceLocalTrack("video", track);
            cameraTrackRef.current = track;
            if (!screenTrackRef.current && localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }
            await loadMediaDevices();
        } catch (err) {
            setError(err.message || "Could not switch camera");
        }
    };

    const switchSpeaker = async (deviceId) => {
        setSelectedSpeakerId(deviceId);
        const remoteVideo = remoteVideoRef.current;
        if (!remoteVideo || typeof remoteVideo.setSinkId !== "function") {
            setError("Speaker selection is not supported in this browser");
            return;
        }

        try {
            await remoteVideo.setSinkId(deviceId || "");
        } catch (err) {
            setError(err.message || "Could not switch speaker");
        }
    };

    const toggleMute = () => {
        localStreamRef.current?.getAudioTracks().forEach((track) => {
            track.enabled = isMuted;
        });
        setIsMuted((value) => !value);
    };

    const toggleCamera = async () => {
        const pc = peerConnectionRef.current;
        if (!pc) return;

        if (cameraTrackRef.current) {
            pc.getSenders()
                .filter((sender) => sender.track === cameraTrackRef.current)
                .forEach((sender) => pc.removeTrack(sender));
            cameraTrackRef.current.stop();
            cameraTrackRef.current = null;
            setIsCameraEnabled(false);
            if (!screenTrackRef.current && localVideoRef.current) localVideoRef.current.srcObject = null;
            return;
        }

        const video = selectedCameraId ? { deviceId: { exact: selectedCameraId } } : true;
        const stream = await navigator.mediaDevices.getUserMedia({ video });
        const [track] = stream.getVideoTracks();
        cameraTrackRef.current = track;
        localStreamRef.current?.addTrack(track);
        pc.addTrack(track, stream);
        if (!screenTrackRef.current && localVideoRef.current) localVideoRef.current.srcObject = stream;
        setIsCameraEnabled(true);
        await loadMediaDevices();
    };

    const startScreenShare = async () => {
        const pc = peerConnectionRef.current;
        if (!pc) return;

        try {
            if (!navigator.mediaDevices?.getDisplayMedia) {
                throw new Error("Screen sharing is not supported in this browser.");
            }

            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            const [track] = stream.getVideoTracks();
            screenTrackRef.current = track;
            pc.addTrack(track, stream);
            if (localVideoRef.current) localVideoRef.current.srcObject = stream;
            setIsSharingScreen(true);
            logRTC("screen-share-track-added", { roomId, signalingState: pc.signalingState });
            sendSignal({ type: "screen-share-started" });

            track.onended = () => stopScreenShare();
        } catch (err) {
            logRTC("screen-share-error", { message: err.message, name: err?.name, roomId });
            setError(err.message || "Could not start screen sharing");
        }
    };

    const stopScreenShare = () => {
        const pc = peerConnectionRef.current;
        const track = screenTrackRef.current;
        if (!pc || !track) return;

        pc.getSenders()
            .filter((sender) => sender.track === track)
            .forEach((sender) => pc.removeTrack(sender));
        track.stop();
        screenTrackRef.current = null;
        setIsSharingScreen(false);
        logRTC("screen-share-track-removed", { roomId, signalingState: pc.signalingState });
        sendSignal({ type: "screen-share-stopped" });

        if (localVideoRef.current) {
            localVideoRef.current.srcObject = cameraTrackRef.current ? new MediaStream([cameraTrackRef.current]) : null;
        }
    };

    const hangUp = () => {
        cleanup();
        onClose();
    };

    const toggleRemoteFullscreen = async () => {
        const panel = remotePanelRef.current;
        if (!panel) return;

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
                setIsRemoteExpanded(false);
                return;
            }

            if (panel.requestFullscreen) {
                await panel.requestFullscreen();
                setIsRemoteExpanded(true);
                return;
            }
        } catch (err) {
            logRTC("remote-fullscreen-error", { message: err.message, roomId });
        }

        setIsRemoteExpanded((value) => !value);
    };

    const sendChatMessage = () => {
        const body = chatInput.trim();
        const channel = dataChannelRef.current;
        if (!body || !channel || channel.readyState !== "open") {
            return;
        }

        const message = {
            body,
            sentAt: new Date().toISOString(),
        };
        channel.send(JSON.stringify(message));
        appendChatMessage({
            from: user.username,
            body,
            sentAt: message.sentAt,
            direction: "outgoing",
        });
        setChatInput("");
    };

    const sendFile = async (file) => {
        const channel = dataChannelRef.current;
        if (!file || !channel || channel.readyState !== "open") {
            return;
        }

        if (file.size > maxFileSize) {
            setError(`File is too large. Limit is ${formatFileSize(maxFileSize)}.`);
            return;
        }

        const fileId = createMessageId();
        const messageId = createMessageId();
        setIsSendingFile(true);
        setError("");
        setChatMessages((messages) => [
            ...messages,
            {
                id: messageId,
                direction: "outgoing",
                file: {
                    name: file.name,
                    progress: 0,
                    size: file.size,
                    status: "Sending",
                },
                from: user.username,
                sentAt: new Date().toISOString(),
            },
        ]);

        try {
            channel.send(JSON.stringify({
                fileId,
                fileType: file.type,
                kind: "file-start",
                name: file.name,
                sentAt: new Date().toISOString(),
                size: file.size,
            }));

            let offset = 0;
            while (offset < file.size) {
                await waitForBufferedAmount(channel);
                const chunkBuffer = await file.slice(offset, offset + fileChunkSize).arrayBuffer();
                channel.send(createFilePacket(fileId, chunkBuffer));
                offset += chunkBuffer.byteLength;
                updateChatMessage(messageId, {
                    file: {
                        name: file.name,
                        progress: Math.min(100, Math.round((offset / file.size) * 100)),
                        size: file.size,
                        status: "Sending",
                    },
                });
            }

            channel.send(JSON.stringify({ fileId, kind: "file-end" }));
            updateChatMessage(messageId, {
                file: {
                    name: file.name,
                    progress: 100,
                    size: file.size,
                    status: "Sent",
                },
            });
        } catch (err) {
            setError(err.message || "Could not send file");
            updateChatMessage(messageId, {
                file: {
                    name: file.name,
                    progress: 0,
                    size: file.size,
                    status: "Failed",
                },
            });
        } finally {
            setIsSendingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleFileSelected = (event) => {
        const [file] = event.target.files || [];
        if (file) {
            sendFile(file);
        }
    };

    const handleChatKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendChatMessage();
        }
    };

    useEffect(() => cleanup, [cleanup]);

    useEffect(() => {
        if (isOpen && autoStart && !hasStarted) {
            startCall();
        }
    }, [autoStart, hasStarted, isOpen, startCall]);

    useEffect(() => {
        if (!isOpen) return undefined;

        loadMediaDevices().catch((err) => setError(err.message || "Could not load media devices"));
        navigator.mediaDevices?.addEventListener?.("devicechange", loadMediaDevices);
        return () => {
            navigator.mediaDevices?.removeEventListener?.("devicechange", loadMediaDevices);
        };
    }, [isOpen, loadMediaDevices]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsRemoteExpanded(document.fullscreenElement === remotePanelRef.current);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    if (!isOpen) return null;

    return (
        <section className="call-workspace">
            <div className="call-modal">
                <div className="call-header">
                    <div>
                        <h2>Call with {contact?.username}</h2>
                        <p>{status}</p>
                    </div>
                    <button className="call-close-button" onClick={hangUp} title="Close call">
                        <X size={18} />
                    </button>
                </div>

                <div className="call-grid">
                    <div className={`video-panel remote-video-panel ${isRemoteExpanded ? "is-expanded" : ""}`} ref={remotePanelRef}>
                        <span>Remote</span>
                        <video ref={remoteVideoRef} autoPlay playsInline />
                        <button
                            className="video-panel-action"
                            onClick={toggleRemoteFullscreen}
                            title={isRemoteExpanded ? "Exit full screen" : "Full screen"}
                        >
                            {isRemoteExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                    </div>
                    <div className="video-panel">
                        <span>You</span>
                        <video ref={localVideoRef} autoPlay muted playsInline />
                    </div>
                </div>

                <div className="call-chat">
                    <div className="call-chat-header">
                        <h3>Chat</h3>
                        <span>{isChatReady ? "Connected" : "Waiting for call connection"}</span>
                    </div>
                    <div className="call-chat-messages" aria-live="polite">
                        {chatMessages.length === 0 ? (
                            <p className="call-chat-empty">Messages in this call will appear here.</p>
                        ) : (
                            chatMessages.map((message) => (
                                <div
                                    className={`call-chat-message ${message.direction === "outgoing" ? "is-outgoing" : ""}`}
                                    key={message.id}
                                >
                                    <strong>{message.direction === "outgoing" ? "You" : message.from}</strong>
                                    {message.file ? (
                                        <span className="file-transfer">
                                            <span className="file-transfer-icon"><FileText size={16} /></span>
                                            <span className="file-transfer-copy">
                                                <span>{message.file.name}</span>
                                                <small>
                                                    {formatFileSize(message.file.size)} · {message.file.status}
                                                    {message.file.progress < 100 ? ` ${message.file.progress}%` : ""}
                                                </small>
                                                {message.file.progress < 100 && (
                                                    <span className="file-transfer-progress">
                                                        <span style={{ width: `${message.file.progress}%` }} />
                                                    </span>
                                                )}
                                            </span>
                                            {message.file.url && (
                                                <a href={message.file.url} download={message.file.name} title="Download file">
                                                    <Download size={16} />
                                                </a>
                                            )}
                                        </span>
                                    ) : (
                                        <span>{message.body}</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="call-chat-composer">
                        <input
                            ref={fileInputRef}
                            className="file-input"
                            type="file"
                            onChange={handleFileSelected}
                            disabled={!isChatReady || isSendingFile}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={!isChatReady || isSendingFile}
                            title="Attach file"
                        >
                            <Paperclip size={16} />
                        </button>
                        <textarea
                            value={chatInput}
                            onChange={(event) => setChatInput(event.target.value)}
                            onKeyDown={handleChatKeyDown}
                            placeholder="Write a message during the call"
                            rows={2}
                            disabled={!isChatReady}
                        />
                        <button onClick={sendChatMessage} disabled={!isChatReady || !chatInput.trim()} title="Send message">
                            <Send size={16} />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="call-error-panel">
                        <p className="error-message">{error}</p>
                        {isAudioPermissionBlocked && (
                            <button onClick={retryMicrophone} className="control-button is-primary">
                                <Mic size={16} />
                                <span>Retry microphone</span>
                            </button>
                        )}
                    </div>
                )}

                {isSettingsOpen && (
                    <div className="device-settings">
                        <div className="device-settings-header">
                            <div>
                                <h3>Call settings</h3>
                                <p>Choose the devices used in this call.</p>
                            </div>
                            <button onClick={loadMediaDevices} title="Refresh devices">
                                <RefreshCw size={16} />
                            </button>
                        </div>
                        <div className="device-settings-grid">
                            <label>
                                <span>Microphone</span>
                                <select
                                    value={selectedMicId}
                                    onChange={(event) => switchMicrophone(event.target.value)}
                                >
                                    {mediaDevices.audioinput.length === 0 ? (
                                        <option value="">Default microphone</option>
                                    ) : (
                                        mediaDevices.audioinput.map((device, index) => (
                                            <option value={device.deviceId} key={device.deviceId || `mic-${index}`}>
                                                {device.label || `Microphone ${index + 1}`}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                            <label>
                                <span>Camera</span>
                                <select
                                    value={selectedCameraId}
                                    onChange={(event) => switchCamera(event.target.value)}
                                >
                                    {mediaDevices.videoinput.length === 0 ? (
                                        <option value="">Default camera</option>
                                    ) : (
                                        mediaDevices.videoinput.map((device, index) => (
                                            <option value={device.deviceId} key={device.deviceId || `camera-${index}`}>
                                                {device.label || `Camera ${index + 1}`}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                            <label>
                                <span>Speaker</span>
                                <select
                                    value={selectedSpeakerId}
                                    onChange={(event) => switchSpeaker(event.target.value)}
                                    disabled={typeof remoteVideoRef.current?.setSinkId !== "function"}
                                >
                                    {mediaDevices.audiooutput.length === 0 ? (
                                        <option value="">Default speaker</option>
                                    ) : (
                                        mediaDevices.audiooutput.map((device, index) => (
                                            <option value={device.deviceId} key={device.deviceId || `speaker-${index}`}>
                                                {device.label || `Speaker ${index + 1}`}
                                            </option>
                                        ))
                                    )}
                                </select>
                            </label>
                        </div>
                    </div>
                )}

                <div className="call-controls">
                    {!hasStarted && <button className="control-button is-primary" onClick={startCall}><Phone size={18} /> Start</button>}
                    <button onClick={toggleMute} disabled={!localStreamRef.current}>
                        {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                        <span>{isMuted ? "Unmute" : "Mute"}</span>
                    </button>
                    <button onClick={toggleCamera} disabled={!peerConnectionRef.current}>
                        {isCameraEnabled ? <VideoOff size={18} /> : <Video size={18} />}
                        <span>{isCameraEnabled ? "Camera off" : "Camera on"}</span>
                    </button>
                    <button
                        onClick={isSharingScreen ? stopScreenShare : startScreenShare}
                        disabled={!peerConnectionRef.current}
                    >
                        <MonitorUp size={18} />
                        <span>{isSharingScreen ? "Stop sharing" : "Share screen"}</span>
                    </button>
                    <button onClick={() => setIsSettingsOpen((value) => !value)}>
                        <Settings size={18} />
                        <span>Settings</span>
                    </button>
                    <button className="control-button is-danger" onClick={hangUp}>
                        <PhoneOff size={18} />
                        <span>Leave</span>
                    </button>
                </div>
            </div>
        </section>
    );
}

export default WebRTCCallSession;
